import './select-input.scss';
import {useContext} from 'react';
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  Box,
  Avatar,
  Button,
  Typography,
  FormHelperText,
  SelectChangeEvent,
} from '@mui/material';
import {AppContext} from '../../contexts/app/context';
import {AppContextType} from '../../contexts/app/types';
import {ThemeMode} from '../../enums/theme-mode';
import {useTranslation} from 'react-i18next';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import {translations} from '../../i18n/translations';
import {isSvg} from '../../utils/functions/validate-uri-svg';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import {Tooltip} from '../tooltip/tooltip';

/**
 * Select input field.
 */
export const SelectInput = (props: {
  label?: string;
  items: Array<{
    value: string;
    label: string;
    avatar?: string;
  }>;
  handleChange: (
    value: string,
    event: SelectChangeEvent | React.MouseEvent<HTMLButtonElement>
  ) => void;
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  id?: string;
  className?: string;
  tooltipTitle?: string;
  notAllowedEditing?: boolean;
  error?: string;
  avatar?: {
    type: 'user' | 'entity' | 'other';
    isIcon?: boolean;
  };
  totalItems?: number;
  loadingMore?: boolean;
  inputSize?: 'standard' | 'small';
  handleFetchMore?: () => void;
  handleBlur?: () => void;
  handleClose?: (event: React.SyntheticEvent) => void;
}) => {
  // Get context
  const appContext: AppContextType = useContext(AppContext);

  // Translations
  const {t} = useTranslation();

  // Get theme
  const theme = useTheme();

  // Render avatar
  const renderAvatar = (isIcon: boolean, item: {value: string; label: string; avatar?: string}) => {
    // Avatar sizes, coefficients and default icon
    const avatarSize: number =
      props.inputSize === 'small'
        ? avatarSizesAndCoefficients.avatarSizes.tiny
        : avatarSizesAndCoefficients.avatarSizes.small;
    const iconSize: number = avatarSize * avatarSizesAndCoefficients.iconSizeCoefficient;
    let defaultAvatarIcon: React.ReactElement = <></>;
    let avatarBorderRadius: string | undefined;
    if (props.avatar) {
      switch (props.avatar.type) {
        case 'user':
          defaultAvatarIcon = <PersonIcon sx={{fontSize: iconSize}} />;
          avatarBorderRadius = `${avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.circle}px`;
          break;
        case 'entity':
          defaultAvatarIcon = <BusinessIcon sx={{fontSize: iconSize}} />;
          avatarBorderRadius = `${
            avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
          }px`;
          break;
        default:
          defaultAvatarIcon = <></>;
          avatarBorderRadius = `${
            avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersHigh
          }px`;
          break;
      }
    }

    return isIcon ? (
      <Avatar
        className="select-input-avatar"
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarBorderRadius,
          borderColor: theme.palette.action.disabled,
          opacity: props.disabled === true || props.notAllowedEditing === true ? 0.38 : 1,
        }}
      >
        {item.avatar ? (
          isSvg(item.avatar) ? (
            <object
              className="select-input-avatar-svg-object"
              data={item.avatar}
              aria-label={item.label}
              type="image/svg+xml"
              width={`${iconSize}`}
              height={`${iconSize}`}
            ></object>
          ) : (
            <img
              style={{
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
              src={item.avatar}
              alt={item.label}
            />
          )
        ) : (
          defaultAvatarIcon
        )}
      </Avatar>
    ) : (
      <Avatar
        className="select-input-avatar"
        src={item.avatar}
        alt={item.label}
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarBorderRadius,
          borderColor: theme.palette.action.disabled,
          opacity: props.disabled === true || props.notAllowedEditing === true ? 0.38 : 1,
        }}
      >
        {defaultAvatarIcon}
      </Avatar>
    );
  };

  // Show clear button
  const showClearButton: boolean =
    props.value &&
    props.value.trim().length > 0 &&
    props.clearable === true &&
    props.disabled !== true &&
    props.notAllowedEditing !== true
      ? true
      : false;

  // Select input
  const selectInput: React.ReactElement = (
    <Box sx={{width: '100%'}} className={!props.tooltipTitle ? props.className : undefined}>
      <FormControl
        fullWidth
        variant="outlined"
        error={props.error ? true : false}
        sx={{
          // Fix border color when hovering on top of the clear button
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border:
              appContext.state.currentThemeMode === ThemeMode.Dark
                ? '1px solid white'
                : '1px solid black',
          },
        }}
      >
        <InputLabel
          disabled={props.disabled === true ? true : false}
          error={props.error ? true : false}
          color="neutral"
          shrink={true}
        >
          {props.required === true ? `${props.label || ''} *` : props.label || undefined}
        </InputLabel>
        <Select
          id={props.id}
          labelId={`${props.id}-label`}
          input={
            <OutlinedInput
              notched
              label={props.required === true ? `${props.label || ''} *` : props.label || undefined}
              error={props.error ? true : false}
            />
          }
          value={props.value || ''}
          displayEmpty={true}
          renderValue={(value) => {
            // Get item
            const item = props.items.find((obj) => obj.value === value);

            // Render
            if (item) {
              // Note: We don't use the startAdornment to show the avatar, because
              // the dropdown with will not be the same as the select input width.
              return (
                <Box className="select-input-value-container">
                  {props.avatar
                    ? renderAvatar(props.avatar.isIcon === true ? true : false, item)
                    : undefined}
                  {item.label}
                </Box>
              );
            } else {
              // Return placeholder if empty
              return props.placeholder || '';
            }
          }}
          onChange={(event) => {
            props.handleChange(event.target.value, event);
          }}
          color="neutral"
          onBlur={props.handleBlur}
          onClose={props.handleClose}
          required={props.required === true ? true : false}
          disabled={props.disabled === true || props.notAllowedEditing === true ? true : false}
          error={props.error ? true : false}
          sx={{
            '& .MuiSelect-select': {
              paddingRight: showClearButton ? '60px !important' : '32px',
              cursor: props.notAllowedEditing === true ? 'not-allowed !important' : undefined,
              paddingTop: props.inputSize === 'small' ? '10px' : undefined,
              paddingBottom: props.inputSize === 'small' ? '10px' : undefined,
            },
            width: '100%',
            height: props.inputSize === 'small' ? '46px' : '56px',
            color: props.value ? theme.palette.text.primary : theme.palette.text.disabled,
          }}
        >
          {props.items.map((obj) => {
            return (
              <MenuItem
                key={`${props.id ? props.id : 'select-input'}-menu-item-${obj.value}`}
                value={obj.value}
              >
                {props.avatar
                  ? renderAvatar(props.avatar.isIcon === true ? true : false, obj)
                  : undefined}
                {obj.label}
              </MenuItem>
            );
          })}
          {props.loadingMore === true && (
            <Box className="select-input-more-button-container">
              <Typography color="GrayText" variant="body1">
                {t(translations.search_select_input.loading)}
              </Typography>
            </Box>
          )}
          {props.handleFetchMore &&
            props.totalItems &&
            props.totalItems > props.items.length &&
            props.loadingMore !== true && (
              <Box className="select-input-more-button-container">
                <Typography color="GrayText" variant="body1">
                  {t(translations.search_select_input.items_showing_information, {
                    value: props.items.length,
                    total: props.totalItems,
                  })}
                </Typography>
                <Box className="select-input-more-button">
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={props.handleFetchMore}
                    endIcon={<AddIcon />}
                  >
                    {t(translations.search_select_input.more_button)}
                  </Button>
                </Box>
              </Box>
            )}
        </Select>
        {showClearButton && (
          <IconButton
            onClick={(event) => props.handleChange('', event)}
            sx={{
              position: 'absolute',
              right: '32px',
              top: props.inputSize === 'small' ? '9px' : '14px',
              width: '28px',
              height: '28px',
            }}
          >
            <ClearIcon sx={{fontSize: '20px'}} />
          </IconButton>
        )}
        {props.error && <FormHelperText>{props.error}</FormHelperText>}
      </FormControl>
    </Box>
  );

  return (
    <>
      {props.tooltipTitle ? (
        <Tooltip className={props.className} title={props.tooltipTitle}>
          {selectInput}
        </Tooltip>
      ) : (
        selectInput
      )}
    </>
  );
};
